import {
    Checkbox,
    createStyles,
    makeStyles,
    MenuItem,
    Select,
    Theme,
    createTheme,
    ThemeProvider,
    FormControlLabel,
} from "@material-ui/core";
import stylesPc from "./FilterWiget.module.css";
import stylesSp from "./FilterWiget_SP.module.css";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import React, { memo, useCallback, useEffect, useState } from "react";
import { VideoFilter } from "../../video/models/VideoFilter";
import PuiTypography from "../../../components/elements/PuiTypography";
import { FilterTag } from "../../../viewer/search/Search";
import { Video } from "../../video/models/Video";
import { Slide } from "../../../models/slides";
import { useInitText } from "../../../components/function/useInitText";
import PuiInput from "../../../components/elements/PuiInput";
import { Search, Check } from "@material-ui/icons";
import { t } from "i18next";

const SELECT_ITEM_HEIGHT = 20;
const SELCECT_ITEM_PADDING_TOP = 8;

interface FilterWigetProps {
    contents: Video[] | Slide[];
    videoFilter: VideoFilter;
    changeProfessionalFilter: (presenterId: string) => void;
    changeTagFilter: (event: React.ChangeEvent<HTMLInputElement>) => void;
    setContentList: (contentList: Video[] | Slide[]) => void;
    isOld: boolean;
    setIsOld: (old: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        tagCheckBox: {
            marginLeft: theme.spacing(1),
        },
        formSelect: {
            border: "1px solid var(--gray-38)",
            width: IsDevice("iphone") ? "130px" : "224px",
            color: "var(--gray-80)",
            height: "40px",
            fontSize: "14px",
            background: "#FFFFFF0D",
            borderRadius: "5px",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "& .MuiSelect-icon": {
                color: "white",
            },
            "& .MuiSelect-selectMenu": {
                color: "white",
            },
        },
        presenterItem: {
            display: "flex",
            backgroundColor: "var(--textfield-bg)",
            color: "var(--primary-font)",
            fontSize: "14px",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "&:hover": {
                color: "white",
                backgroundColor: "#35393F",
            },
        },
        checkBox: {
            marginLeft: "13px",
            color: "var(--white)",
        },
        menuItem: {
            backgroundColor: "var(--textfield-bg)",
            color: "white",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "&:hover": {
                color: "white",
                backgroundColor: "#FFFFFF0D",
            },
        },
        dateItem: {
            display: "flex",
            backgroundColor: "var(--textfield-bg)",
            color: "white",
            fontSize: "14px",
            padding: "11px 16px",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "&:hover": {
                color: "white",
                backgroundColor: "#35393F",
            },
            ".Mui-selected": {
                backgroundColor: "#35393F",
            },
        },
        dropdownStyle: {
            color: "white",
            backgroundColor: "var(--textfield-bg)",
            marginTop: "48px",
        },
        searchDropdownStyle: {
            marginTop: "67px",
            color: "white",
            backgroundColor: "var(--textfield-bg)",
            height: "317px",
            width: "224px",
            "& .MuiList-padding": {
                paddingTop: "0",
            },
        },
    })
);

const theme = createTheme({
    palette: {
        primary: {
            main: "#16ADC1",
        },
        secondary: {
            main: "#4395EA",
        },
    },
});

function FilterWiget({
    contents,
    videoFilter,
    changeProfessionalFilter,
    changeTagFilter,
    setContentList,
    isOld,
    setIsOld,
}: FilterWigetProps) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : stylesSp;
    const classes = useStyles();

    //プレゼンター表示
    const [professionals, setProfessionals] = useState<string[]>([]);

    const changeProfessionalHandle = useCallback(
        (presenterId: string) => {
            changeProfessionalFilter(presenterId);
        },
        [changeProfessionalFilter]
    );

    const changeTagHandle = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (changeTagFilter) {
                changeTagFilter(event);
            }
        },
        [changeTagFilter]
    );

    //キーワード表示
    const [tags, setTags] = useState<FilterTag[]>([]);

    useEffect(() => {
        if (Array.isArray(contents)) {
            const pList = [""];
            const tagList: string[] = [];
            const filterTags: FilterTag[] = [];
            contents.forEach((element: Video | Slide) => {
                // プレゼンター
                if (!pList.includes(element.presenter)) {
                    pList.push(element.presenter);
                }
                // tag表示をvideoとslidestockを分ける
                for (let i = 0; i < element.tags.length; i++) {
                    if (!tagList.includes(element.tags[i])) {
                        tagList.push(element.tags[i]);
                        const damiTag = {
                            tag: element.tags[i],
                            tagCount: 1,
                        };
                        filterTags.push(damiTag);
                    } else {
                        const index = filterTags.findIndex((item) => item.tag === element.tags[i]);
                        filterTags[index].tagCount += 1;
                    }
                }
            });
            setProfessionals(pList);
            setTags(filterTags);
        }
    }, [contents]);

    // 検索する対象のキーワードリスト
    const [searchTargetKeywordList, setSearchTargetKeywordList] = useState<string[]>([]);

    // 検索表示keywordList
    const [searchKeywordList, setSearchKeywordList] = useState<string[]>([]);

    // 検索word
    const [searchWord, setSearchWord] = useInitText("");

    // BEから取得したtagListをstateに格納
    useEffect(() => {
        if (tags) {
            setSearchTargetKeywordList(tags.map((tag) => tag.tag));
            setSearchKeywordList(tags.map((tag) => tag.tag));
        }
    }, [tags]);

    // inputChangeEvent
    const handleInputSearchKeyword = (value: string) => {
        setSearchWord(value);
        setSearchKeywordList(searchTargetKeywordList.filter((tag) => tag.includes(value)));
    };

    // 並び順filter
    const [filterDate, setFilterDate] = useState<string>(String(t("新着順")));

    // 並び順filter押下時
    const handleSortDate = (old: boolean, filter: string) => {
        // コンテンツ順番設定
        const sortedContentList = contents.sort((a: Video | Slide, b: Video | Slide) => {
            if (old) {
                return a.id - b.id;
            } else {
                return b.id - a.id;
            }
        });
        setContentList(sortedContentList);
        setIsOld(old);
        setFilterDate(filter);
    };

    return (
        <div className={styles.widget}>
            <div className={styles.condArea}>
                <ThemeProvider theme={theme}>
                    <div className={styles.selectFilter}>
                        <Select
                            required
                            color="secondary"
                            id="presenter-select"
                            variant="outlined"
                            onChange={(event) =>
                                changeProfessionalHandle(event.target.value as string)
                            }
                            value={videoFilter.professional[0]}
                            defaultValue={t("プレゼンター")}
                            renderValue={(selected: any) =>
                                selected.length === 0 ? <p>{t("プレゼンター")}</p> : selected
                            }
                            MenuProps={{
                                getContentAnchorEl: null,
                                anchorOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                classes: {
                                    paper: classes.searchDropdownStyle,
                                },
                            }}
                            className={classes.formSelect}
                        >
                            <MenuItem disabled value={t("プレゼンター")}>
                                <p>{t("プレゼンター")}</p>
                            </MenuItem>
                            {professionals.length > 0 &&
                                professionals.slice(1).map((value, index) => (
                                    <FormControlLabel
                                        key={index}
                                        value={value}
                                        className={classes.presenterItem}
                                        style={{
                                            color:
                                                videoFilter.professional[0] === value
                                                    ? "white"
                                                    : "rgba(255, 255, 255, 0.6)",
                                            backgroundColor:
                                                videoFilter.professional[0] === value
                                                    ? "#35393F"
                                                    : "",
                                        }}
                                        control={
                                            <Checkbox
                                                className={classes.checkBox}
                                                color="primary"
                                                checked={videoFilter.professional[0] === value}
                                            />
                                        }
                                        label={value}
                                    />
                                ))}
                        </Select>
                    </div>
                    <div className={styles.selectFilter}>
                        <Select
                            color="secondary"
                            id="tag-select"
                            variant="outlined"
                            value={videoFilter.tag?.map((tag) => tag)}
                            multiple
                            displayEmpty
                            renderValue={(selected: any) => {
                                if (selected.length === 0) {
                                    return <p>{t("タグ")}</p>;
                                }

                                return selected.join(", ");
                            }}
                            MenuProps={{
                                getContentAnchorEl: null,
                                anchorOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                classes: {
                                    paper: classes.searchDropdownStyle,
                                },
                            }}
                            className={classes.formSelect}
                            style={{
                                maxHeight: SELECT_ITEM_HEIGHT * 4.5 + SELCECT_ITEM_PADDING_TOP,
                            }}
                        >
                            <div className={styles.selectKeyword}>
                                <Search
                                    className={styles.searchIcon}
                                    style={{
                                        color: "var(--white)",
                                        position: "absolute",
                                        marginTop: "13px",
                                        marginLeft: "194px",
                                    }}
                                />
                                <PuiInput
                                    placeholder={String(t("検索する"))}
                                    className={styles.keywordInput}
                                    value={searchWord}
                                    handleChange={handleInputSearchKeyword}
                                />
                            </div>
                            <MenuItem disabled value="">
                                <p>{t("タグ")}</p>
                            </MenuItem>
                            <div className={styles.keywordList}>
                                {searchKeywordList.length > 0 ? (
                                    searchKeywordList.map((keyword) => (
                                        <div
                                            key={keyword}
                                            className={styles.keyword}
                                            onClick={() => changeTagHandle}
                                        >
                                            <Checkbox
                                                color="primary"
                                                checked={videoFilter.tag.includes(keyword)}
                                                onChange={changeTagHandle}
                                                name={keyword}
                                                className={classes.menuItem}
                                            />
                                            <PuiTypography size={14} color={"primary-font"}>
                                                {keyword}
                                            </PuiTypography>
                                        </div>
                                    ))
                                ) : (
                                    <div className={styles.noResult}>
                                        <PuiTypography size={14} color={"primary-font"}>
                                            {t("タグが見つかりません")}
                                        </PuiTypography>
                                    </div>
                                )}
                            </div>
                        </Select>
                    </div>

                    <div className={styles.selectFilter}>
                        <Select
                            color="secondary"
                            id="date-order-select"
                            variant="outlined"
                            value={filterDate}
                            onChange={() => setFilterDate(!isOld ? "新着順" : "古い順")}
                            defaultValue={t("新着順")}
                            className={classes.formSelect}
                            renderValue={() => (!isOld ? t("新着順") : t("古い順"))}
                            MenuProps={{
                                getContentAnchorEl: null,
                                anchorOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                classes: {
                                    paper: classes.dropdownStyle,
                                },
                            }}
                            style={{
                                width: "160px",
                                display: "flex",
                            }}
                        >
                            <MenuItem
                                value={"新着順"}
                                className={classes.dateItem}
                                onClick={() => handleSortDate(false, "新着順")}
                            >
                                {t("新着順")}
                                {!isOld && <Check className={styles.checkIcon} id="newcheckIcon" />}
                            </MenuItem>
                            <MenuItem
                                value="古い順"
                                className={classes.dateItem}
                                onClick={() => handleSortDate(true, "古い順")}
                            >
                                {t("古い順")}
                                {isOld && <Check className={styles.checkIcon} id="oldcheckIcon" />}
                            </MenuItem>
                        </Select>
                    </div>
                </ThemeProvider>
            </div>
        </div>
    );
}

export default memo(FilterWiget);
